import {
  CouponRefundPendingRow,
  CouponSendPendingRow,
  GiftCouponSettlementDetailResponse,
} from '@shared/api/couponSettlement/couponSettlement.interface';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { couponCodeLabel } from './coupon-settlement.utils';
import { formatDate } from './delivery.utils';
import { statusLabel } from './rebate-settlement.utils';

// ─────────────────────────────────────────────
// 시트 마무리 헬퍼 — giftSettlementHistoryExcel/rebateSettlementExcel과 동일 idiom.
// 셀 색/굵기 스타일은 커뮤니티 xlsx 미지원이라, 가독성은 지원 범위(내용 기반 열 너비·
// 행 높이·숫자 천단위 포맷·헤더 고정·자동 필터)로 확보한다.
// ─────────────────────────────────────────────

const NUM_FMT = '#,##0';
/** 수수료는 이제 건별 올림된 정수(2026-07 개정)지만, 개정 전 문서의 소수 잔재를 위해 소수 유지 포맷을 남겨둔다 */
const FEE_FMT = '#,##0.###';
const MONEY_LABELS = ['판매가', '정상가', '도매가', '정산금', '차감액'];
const FEE_LABELS = ['수수료'];
const HEADER_ROW_PT = 28;
const BODY_ROW_PT = 22;

/** 컬럼 폭 자동 조정 (한글 2.2칸/영문 1칸 기준, 숫자는 콤마 폭 보정) */
function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 8;
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v == null) continue;
      let text = String(cell.v);
      if (typeof cell.v === 'number') {
        const digits = Math.abs(Math.trunc(cell.v)).toString().length;
        text += ','.repeat(Math.max(0, Math.floor((digits - 1) / 3)));
      }
      let w = 0;
      for (const ch of text) w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
      if (w > max) max = w;
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 4, 60) });
  }
  ws['!cols'] = cols;
}

/** 헤더는 높게, 데이터 행은 넉넉히 */
function setRowHeights(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const rows: XLSX.RowInfo[] = [];
  for (let R = range.s.r; R <= range.e.r; R++) {
    rows[R] = { hpt: R === 0 ? HEADER_ROW_PT : BODY_ROW_PT };
  }
  ws['!rows'] = rows;
}

/** 헤더 라벨이 금액 컬럼이면 #,##0 (수수료는 소수 유지) 숫자 포맷 적용 */
function applyMoneyFormat(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const fmtByCol = new Map<number, string>();
  for (let C = range.s.c; C <= range.e.c; C++) {
    const hdr = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (!hdr) continue;
    if (MONEY_LABELS.includes(String(hdr.v))) fmtByCol.set(C, NUM_FMT);
    else if (FEE_LABELS.includes(String(hdr.v))) fmtByCol.set(C, FEE_FMT);
  }
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    fmtByCol.forEach((fmt, C) => {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && typeof cell.v === 'number') cell.z = fmt;
    });
  }
}

/** 헤더 행(1행) 고정 */
function freezeHeader(ws: XLSX.WorkSheet) {
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  if (!ws['!views']) ws['!views'] = [];
  (ws['!views'] as Array<Record<string, unknown>>)[0] = { state: 'frozen', ySplit: 1 };
}

/** 헤더 행에 자동 필터 — 합계 행이 있으면 정렬/필터에 휩쓸리지 않게 범위에서 제외 */
function applyAutoFilter(ws: XLSX.WorkSheet, hasTotalRow: boolean) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  if (hasTotalRow && range.e.r > range.s.r) range.e.r -= 1;
  ws['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
}

/** 폭/높이/숫자포맷/헤더고정/자동필터를 한 번에 적용해 시트를 마무리한다 */
function finishSheet(ws: XLSX.WorkSheet, opts?: { totalRow?: boolean }) {
  autoWidth(ws);
  setRowHeights(ws);
  applyMoneyFormat(ws);
  freezeHeader(ws);
  applyAutoFilter(ws, opts?.totalRow === true);
}

/**
 * 쿠폰 정산서 한 달 상세를 엑셀로 내보낸다.
 * `:id/detail` 응답의 행 1건당 1행 — 상세 모달(CouponSettlementLineTable)과 동일한
 * 컬럼 구성·순서(구매자/고인/구매일/수령상품/정상가 포함, 미정산 엑셀과 같은 라벨).
 * 수수료·수익금·정렬(발송→환불) 전부 서버 확정값 그대로(프론트 재계산 없음).
 * 구매수량 초과 제외분(excludedRows)은 싣지 않는다 — 지급 정산 데이터가 아니라
 * (구매금액 0원 건, 도매가는 낙전 정산에서 상조 상계) 화면 섹션으로만 확인한다
 * (2026-07-02 결정 — 처음의 "초과제외" 별도 시트는 폐기). 재발행여부도 화면
 * 뱃지로만 표시한다. 프로젝트 표준(커뮤니티 xlsx + file-saver)을 따른다.
 */
export const exportCouponSettlementDetailExcel = (
  detail: GiftCouponSettlementDetailResponse,
): void => {
  const { settlement: s, rows } = detail;

  const excelRows = rows.map((row, idx) => ({
    순번: idx + 1,
    구분: row.isRefund ? '환불 차감' : '발송 수익금',
    쿠폰번호: couponCodeLabel(row.fullCode),
    구매상품명: row.purchaseItemName ?? '',
    구매자: row.buyerName ?? '',
    구매자연락처: phoneNumberStringFormator(row.buyerPhoneNumber),
    고인명: row.deceasedName ?? '',
    발인일: row.coffinOutDate ? formatDate(row.coffinOutDate) : '',
    구매일: row.purchasedAt ? formatDate(row.purchasedAt) : '',
    발송일: row.deliveredAt ? formatDate(row.deliveredAt) : '',
    환불일: row.refundedAt ? formatDate(row.refundedAt) : '',
    판매가: row.priceRetail ?? ('' as unknown as number),
    수령상품: row.receiveItemName ?? '',
    정상가: row.priceRegular ?? ('' as unknown as number),
    도매가: row.priceWhole ?? ('' as unknown as number),
    수수료: row.fee ?? ('' as unknown as number),
    정산금: row.amount,
    소속: row.teamName ?? '',
    지도사명: row.workerName ?? '',
    지도사연락처: phoneNumberStringFormator(row.workerPhoneNumber),
  }));

  excelRows.push({
    순번: '' as unknown as number,
    구분: '합계',
    쿠폰번호: '',
    구매상품명: '',
    구매자: '',
    구매자연락처: '',
    고인명: '',
    발인일: '',
    구매일: '',
    발송일: '',
    환불일: '',
    판매가: '' as unknown as number,
    수령상품: '',
    정상가: '' as unknown as number,
    도매가: '' as unknown as number,
    수수료: s.totalFee ?? ('' as unknown as number),
    정산금: s.totalAmount,
    소속: '',
    지도사명: '',
    지도사연락처: '',
  });

  const ws = XLSX.utils.json_to_sheet(excelRows);
  finishSheet(ws, { totalRow: true });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '쿠폰정산');

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([out], { type: 'application/octet-stream' }),
    `쿠폰정산_${s.year}년${s.month}월_${statusLabel(s.status)}.xlsx`,
  );
};

/**
 * 미정산 상세(상조 1곳)의 발송·환불 raw를 시트 2장으로 내보낸다.
 * 구매수량 초과(overQuota) 발송은 싣지 않는다 — 지급 정산 데이터가 아니라
 * (구매금액 0원 건, 도매가는 낙전 정산에서 상조 상계) 화면 표기로만 확인한다
 * (2026-07-02 결정 — 처음의 "초과여부" 칼럼 포함은 폐기, 가이드 §3-5).
 * 값은 전부 서버 확정치 그대로.
 */
export const exportCouponPendingExcel = (
  brandName: string,
  sendRows: CouponSendPendingRow[],
  refundRows: CouponRefundPendingRow[],
): void => {
  const sendSheet = sendRows
    .filter((r) => r.overQuota !== true)
    .map((r, idx) => ({
      순번: idx + 1,
      쿠폰번호: couponCodeLabel(r.fullCode),
      구매상품명: r.purchaseItemName ?? '',
      구매자: r.buyerName ?? '',
      구매자연락처: phoneNumberStringFormator(r.buyerPhoneNumber),
      고인명: r.deceasedName ?? '',
      발인일: r.coffinOutDate ? formatDate(r.coffinOutDate) : '',
      구매일: r.purchasedAt ? formatDate(r.purchasedAt) : '',
      발송일: r.deliveredAt ? formatDate(r.deliveredAt) : '',
      판매가: r.priceRetail ?? ('' as unknown as number),
      수령상품: r.receiveItemName ?? '',
      정상가: r.priceRegular ?? ('' as unknown as number),
      도매가: r.priceWhole ?? ('' as unknown as number),
      수수료: r.fee ?? ('' as unknown as number),
      정산금: r.amount,
      소속: r.teamName ?? '',
      지도사명: r.workerName ?? '',
      지도사연락처: phoneNumberStringFormator(r.workerPhoneNumber),
    }));

  const refundSheet = refundRows.map((r, idx) => ({
    순번: idx + 1,
    쿠폰번호: couponCodeLabel(r.fullCode),
    구매상품명: r.purchaseItemName ?? '',
    구매자: r.buyerName ?? '',
    구매자연락처: phoneNumberStringFormator(r.buyerPhoneNumber),
    고인명: r.deceasedName ?? '',
    발인일: r.coffinOutDate ? formatDate(r.coffinOutDate) : '',
    구매일: r.purchasedAt ? formatDate(r.purchasedAt) : '',
    발송일: r.deliveredAt ? formatDate(r.deliveredAt) : '',
    환불일: r.refundedAt ? formatDate(r.refundedAt) : '',
    차감액: -r.amount,
    소속: r.teamName ?? '',
    지도사명: r.workerName ?? '',
    지도사연락처: phoneNumberStringFormator(r.workerPhoneNumber),
  }));

  const wb = XLSX.utils.book_new();
  const wsSend = XLSX.utils.json_to_sheet(sendSheet);
  finishSheet(wsSend);
  XLSX.utils.book_append_sheet(wb, wsSend, '발송 수익금');
  const wsRefund = XLSX.utils.json_to_sheet(refundSheet);
  finishSheet(wsRefund);
  XLSX.utils.book_append_sheet(wb, wsRefund, '환불 차감');

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([out], { type: 'application/octet-stream' }),
    `쿠폰미정산_${brandName}.xlsx`,
  );
};
